import { Layout } from "../../components/Layout";
import { CapAwareButton } from "../../components/CapAwareButton";

import { PageHeader } from "../../components/PageHeader";
import { useState } from "react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { formatMoney } from "../../lib/api";
import { MoneyAmount } from "../../components/MoneyAmount";
import { MoneyTotal } from "../../components/MoneyTotal";
import { useGoals, useGoalsMutations, useProfile, useFxRates, useFxConvert, useCashAccounts } from "../../hooks/queries";
import { DatePicker } from "../../components/DatePicker";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { toast } from "sonner";
import { reportValidationError } from "../../lib/validationTelemetry";
import { Plus, Target, Plane, GraduationCap, Gift, PiggyBank, ShoppingBag, Pencil, Trash2, PlusCircle, Repeat } from "lucide-react";

const KINDS = [
  { v: "travel", labelEn: "Travel", labelEs: "Viajes", Icon: Plane },
  { v: "study", labelEn: "Studies", labelEs: "Estudios", Icon: GraduationCap },
  { v: "gift", labelEn: "Gift", labelEs: "Regalos", Icon: Gift },
  { v: "emergency_fund", labelEn: "Emergency fund", labelEs: "Fondo emergencia", Icon: PiggyBank },
  { v: "purchase", labelEn: "Purchase", labelEs: "Compra", Icon: ShoppingBag },
  { v: "other", labelEn: "Other", labelEs: "Otro", Icon: Target },
];

const getKind = (v) => KINDS.find((k) => k.v === v) || KINDS[KINDS.length - 1];

export default function Goals() {
  const { t, lang } = useLang();
  const { data: items = [] } = useGoals();
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";
  const { create, update, remove: removeMut, contribute } = useGoalsMutations();
  const { data: fxInfo } = useFxRates();
  const fxList = Object.keys(fxInfo?.rates || {}).sort();
  const fxConvert = useFxConvert();
  const { data: cashAccounts = [] } = useCashAccounts();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [contribTarget, setContribTarget] = useState(null);
  const [contribAmount, setContribAmount] = useState("");
  const [form, setForm] = useState({
    name: "", kind: "travel", target_amount: "", current_amount: "", target_date: "", currency: "", notes: "",
    // iter-169zzg — recurring contribution scheduler (mirrors investments DCA UI).
    contribution_amount: "", contribution_frequency: "", contribution_account_id: "", contribution_start_date: "",
  });
  // iter-169zze · field-level error map → inline highlight instead of toast.
  const [fieldErrors, setFieldErrors] = useState({});

  const openNew = () => {
    setEditing(null);
    setFieldErrors({});
    setForm({
      name: "", kind: "travel", target_amount: "", current_amount: "", target_date: "", currency: "", notes: "",
      contribution_amount: "", contribution_frequency: "", contribution_account_id: "", contribution_start_date: "",
    });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setFieldErrors({});
    setForm({
      name: it.name, kind: it.kind,
      target_amount: String(it.target_amount),
      current_amount: String(it.current_amount),
      target_date: it.target_date ? it.target_date.slice(0, 10) : "",
      currency: it.currency || "",
      notes: it.notes || "",
      contribution_amount: it.contribution_amount != null ? String(it.contribution_amount) : "",
      contribution_frequency: it.contribution_frequency || "",
      contribution_account_id: it.contribution_account_id || "",
      contribution_start_date: it.contribution_start_date ? it.contribution_start_date.slice(0, 10) : "",
    });
    setOpen(true);
  };

  const save = async () => {
    const fe = {};
    const isEs = lang === "es";
    if (!form.name?.trim()) fe.name = isEs ? "El nombre es obligatorio." : "Name is required.";
    if (!form.target_amount || Number(form.target_amount) <= 0) {
      fe.target_amount = isEs ? "Indica un monto meta mayor a 0." : "Enter a target greater than 0.";
    }
    if (Object.keys(fe).length) {
      setFieldErrors(fe);
      reportValidationError("goal-form", fe);
      toast.error(isEs ? "Faltan campos obligatorios." : "Missing required fields.");
      return;
    }
    setFieldErrors({});
    const payload = {
      name: form.name, kind: form.kind,
      target_amount: Number(form.target_amount),
      current_amount: Number(form.current_amount || 0),
      target_date: form.target_date || null,
      currency: form.currency || currency,
      notes: form.notes || "",
      // iter-169zzg — scheduler. Send the full quartet only when the
      // user opted in (amount > 0 AND frequency). Otherwise null-ify
      // so the backend cascades the pending auto-payment delete.
      contribution_amount: form.contribution_amount && Number(form.contribution_amount) > 0
        ? Number(form.contribution_amount)
        : null,
      contribution_frequency: form.contribution_frequency || null,
      contribution_account_id: form.contribution_account_id || null,
      contribution_start_date: form.contribution_start_date || null,
    };
    try {
      if (editing) await update.mutateAsync({ id: editing.id, payload });
      else await create.mutateAsync(payload);
      toast.success(tx(lang, "Saved", "Guardado", "Salvo"));
      setOpen(false);
    } catch (e) { console.error("save goal failed", e); /* onError toast already fired */ }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    await removeMut.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
    toast.success(tx(lang, "Deleted", "Eliminado", "Excluído"));
  };

  const submitContrib = async () => {
    const amt = Number(contribAmount);
    if (!amt || amt <= 0) return toast.error(tx(lang, "Invalid amount", "Monto inválido", "Valor inválido"));
    try {
      await contribute.mutateAsync({ id: contribTarget.id, amount: amt });
      toast.success(tx(lang, "Contribution recorded", "Aporte registrado", "Aporte registrado"));
      setContribTarget(null); setContribAmount("");
    } catch (e) { console.error("contribute failed", e); /* onError toast already fired */ }
  };

  const totalTarget = items.reduce((s, g) => s + fxConvert(Number(g.target_amount || 0), g.currency || currency, currency), 0);
  const totalSaved = items.reduce((s, g) => s + fxConvert(Number(g.current_amount || 0), g.currency || currency, currency), 0);
  const overallPct = totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0;

  return (
    <Layout>
      <PageHeader
        eyebrow={tx(lang, "Saving with purpose", "Ahorro con propósito", "Economia com propósito")}
        title={tx(lang, "Goals", "Metas", "Metas")}
        subtitle={tx(lang,
          `${items.length} ${items.length === 1 ? "goal" : "goals"} — contribute and see how far you are.`,
          `${items.length} ${items.length === 1 ? "meta" : "metas"} — aporta y mira cuánto te falta.`,
          `${items.length} ${items.length === 1 ? "meta" : "metas"} — aporte e veja o quanto falta.`,
        )}
        testid="goals-header"
        resourceKey="active_goals"
        actions={
          <CapAwareButton
            resourceKey="active_goals"
            featureLabel={tx(lang, "active goals", "metas activas", "metas ativas")}
            isEs={lang === "es"}
            hideBadge
            data-testid="add-goal-btn"
            onClick={openNew}
            className="bg-emerald-600 hover:bg-emerald-700 rounded-xl gap-2"
          >
            <Plus className="h-4 w-4" /> {tx(lang, "New goal", "Nueva meta", "Nova meta")}
          </CapAwareButton>
        }
      />

      {items.length > 0 && (
        <Card className="p-5 rounded-2xl border-slate-200 mb-6">
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{tx(lang, "Overall progress", "Progreso total", "Progresso total")}</span>
            <div className="flex items-center gap-2">
              <MoneyTotal
                items={items.map((g) => ({ amount: g.current_amount || 0, currency: g.currency || currency }))}
                currency={currency}
                size="sm"
                testid="goals-total-saved"
              />
              <span className="text-slate-400">/</span>
              <MoneyTotal
                items={items.map((g) => ({ amount: g.target_amount || 0, currency: g.currency || currency }))}
                currency={currency}
                size="sm"
                testid="goals-total-target"
              />
            </div>
          </div>
          <Progress value={overallPct} className="h-3" />
          <div className="mt-1 text-right text-xs font-semibold text-emerald-700">{overallPct.toFixed(1)}%</div>
        </Card>
      )}

      <div className="grid sm:grid-cols-2 gap-3" data-testid="goals-list">
        {items.length === 0 && (
          <Card className="p-8 text-center text-slate-500 rounded-2xl sm:col-span-2">
            {tx(lang, "No goals yet. Create your first one.", "Aún no tienes metas. Crea tu primera.", "Você ainda não tem metas. Crie a primeira.")}
          </Card>
        )}
        {items.map((g) => {
          const pct = Number(g.target_amount) > 0 ? (Number(g.current_amount) / Number(g.target_amount)) * 100 : 0;
          const remaining = Math.max(0, Number(g.target_amount) - Number(g.current_amount));
          const kind = getKind(g.kind);
          const Icon = kind.Icon;
          return (
            <Card key={g.id} data-testid={`goal-${g.id}`} className="p-5 rounded-2xl border-slate-200 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-2xl bg-sky-50 text-sky-700 flex items-center justify-center shrink-0">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-slate-900 truncate">{g.name}</div>
                    <div className="text-xs text-slate-500">
                      {tx(lang, kind.labelEn, kind.labelEs, kind.labelPt || kind.labelEs)}
                      {g.target_date && ` • ${new Date(g.target_date).toLocaleDateString()}`}
                    </div>
                    {g.notes && (
                      <div
                        data-testid={`goal-notes-${g.id}`}
                        className="text-[11px] text-slate-500 mt-0.5 italic truncate max-w-xs"
                        title={g.notes}
                      >
                        “{g.notes}”
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(g)} data-testid={`edit-goal-${g.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(g)} data-testid={`delete-goal-${g.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </div>
              </div>

              <div className="mt-4">
                <div className="flex justify-between text-xs text-slate-500 mb-1 items-start gap-2">
                  <MoneyAmount
                    amount={fxConvert(Number(g.current_amount || 0), g.currency || currency, currency)}
                    currency={currency}
                    original_currency={g.currency && g.currency !== currency ? g.currency : null}
                    original_amount={g.currency && g.currency !== currency ? g.current_amount : null}
                    fx_rate={g.fx_rate}
                    size="sm"
                    showFxLine
                    testid={`goal-current-${g.id}`}
                    className="!items-start"
                  />
                  <MoneyAmount
                    amount={fxConvert(Number(g.target_amount || 0), g.currency || currency, currency)}
                    currency={currency}
                    original_currency={g.currency && g.currency !== currency ? g.currency : null}
                    original_amount={g.currency && g.currency !== currency ? g.target_amount : null}
                    fx_rate={g.fx_rate}
                    size="sm"
                    showFxLine
                    testid={`goal-target-${g.id}`}
                  />
                </div>
                <Progress value={pct} className="h-3" />
                <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                  <span className="text-sm font-semibold text-slate-900 tabular-nums">{pct.toFixed(1)}%</span>
                  <span className="text-xs text-slate-500">
                    {tx(lang, "Remaining ", "Faltan ", "Faltam ")}
                    {g.currency && g.currency !== currency ? "≈ " : ""}
                    {formatMoney(fxConvert(remaining, g.currency || currency, currency), currency)}
                  </span>
                </div>
              </div>

              <Button
                data-testid={`contribute-${g.id}`}
                size="sm"
                variant="outline"
                onClick={() => { setContribTarget(g); setContribAmount(""); }}
                className="mt-4 w-full gap-2"
              >
                <PlusCircle className="h-4 w-4" /> {tx(lang, "Add contribution", "Aportar", "Aportar")}
              </Button>
            </Card>
          );
        })}
      </div>

      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        testid="goal-dialog"
        size="md"
        title={editing ? tx(lang, "Edit goal", "Editar meta", "Editar meta") : tx(lang, "New goal", "Nueva meta", "Nova meta")}
        description={tx(lang, "Capture your goal details", "Captura los datos de tu meta", "Preencha os dados da sua meta")}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11">{t("cancel")}</Button>
            <Button data-testid="goal-form-save" onClick={save} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5 font-semibold">{t("save")}</Button>
          </div>
        }
      >
        <div className="space-y-3">
            <div>
              <Label htmlFor="goal-form-name" className="flex items-center gap-1">
                {tx(lang, "Name", "Nombre", "Nome")}
                <span className="text-rose-500" aria-hidden="true">*</span>
              </Label>
              <Input
                id="goal-form-name"
                data-testid="goal-form-name"
                value={form.name}
                onChange={(e) => { setForm({ ...form, name: e.target.value }); if (fieldErrors.name) setFieldErrors({ ...fieldErrors, name: undefined }); }}
                placeholder={tx(lang, "Trip to Europe", "Viaje a Europa", "Viagem à Europa")}
                aria-invalid={fieldErrors.name ? "true" : undefined}
                className={`h-12 text-base rounded-xl mt-1.5 ${fieldErrors.name ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}`.trim()}
              />
              {fieldErrors.name && (
                <p role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="goal-form-name-error">{fieldErrors.name}</p>
              )}
            </div>
            <div>
              <Label>{tx(lang, "Type", "Tipo", "Tipo")}</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="goal-form-kind" className="h-12 mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => <SelectItem key={k.v} value={k.v}>{tx(lang, k.labelEn, k.labelEs, k.labelPt || k.labelEs)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="goal-form-target" className="flex items-center gap-1">
                  {tx(lang, "Target amount", "Meta total", "Valor da meta")}
                  <span className="text-rose-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="goal-form-target"
                  data-testid="goal-form-target"
                  type="number"
                  inputMode="decimal"
                  value={form.target_amount}
                  onChange={(e) => { setForm({ ...form, target_amount: e.target.value }); if (fieldErrors.target_amount) setFieldErrors({ ...fieldErrors, target_amount: undefined }); }}
                  aria-invalid={fieldErrors.target_amount ? "true" : undefined}
                  className={`h-12 text-base rounded-xl mt-1.5 ${fieldErrors.target_amount ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}`.trim()}
                />
                {fieldErrors.target_amount && (
                  <p role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="goal-form-target-error">{fieldErrors.target_amount}</p>
                )}
              </div>
              <div>
                <Label>{tx(lang, "Saved so far", "Ahorrado", "Economizado")}</Label>
                <Input data-testid="goal-form-current" type="number" inputMode="decimal" value={form.current_amount} onChange={(e) => setForm({ ...form, current_amount: e.target.value })} className="h-12 text-base rounded-xl mt-1.5" />
              </div>
            </div>
            <div>
              <Label>{tx(lang, "Target date", "Fecha objetivo", "Data alvo")}</Label>
              <DatePicker
                testid="goal-form-date"
                value={form.target_date}
                onChange={(v) => setForm({ ...form, target_date: v })}
                placeholder={tx(lang, "Pick a date", "Selecciona fecha", "Escolha uma data")}
              />
            </div>
            <div>
              <Label>{tx(lang, "Currency", "Moneda", "Moeda")}</Label>
              <Select
                value={form.currency || currency}
                onValueChange={(v) => setForm({ ...form, currency: v === currency ? "" : v })}
              >
                <SelectTrigger data-testid="goal-form-currency" className="h-12 mt-1.5 rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {(fxList.length ? fxList : [currency]).map((c) => (
                    <SelectItem key={c} value={c}>{c}{c === currency ? ` · ${tx(lang, "primary", "principal", "principal")}` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{tx(lang, "Notes", "Notas", "Notas")}</Label>
              <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1.5 rounded-xl" />
            </div>

            {/* iter-169zzg · Scheduler de aportes recurrentes (opcional).
                Cuando el usuario completa amount + frequency, el backend
                materializa un Pago "Aporte a meta: {nombre}" que aparece
                en /Pagos. Al marcarlo pagado, sube `current_amount` y se
                spawna el siguiente ciclo. */}
            <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-3 space-y-3" data-testid="goal-scheduler-block">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                <Repeat className="h-4 w-4" />
                {tx(lang, "Recurring contributions (optional)", "Aportes automáticos (opcional)", "Aportes recorrentes (opcional)")}
              </div>
              <p className="text-[11px] text-slate-600 leading-snug">
                {tx(lang,
                  "Schedule a recurring contribution. It will show up in /Payments and bump this goal when you mark it paid.",
                  "Programa un aporte recurrente. Aparecerá en /Pagos y al marcarlo pagado actualizará tu meta automáticamente.",
                  "Agende um aporte recorrente. Vai aparecer em /Pagamentos e atualizará sua meta quando você marcar como pago.",
                )}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="goal-form-contrib-amount" className="text-xs">
                    {tx(lang, "Amount per contribution", "Monto por aporte", "Valor por aporte")}
                  </Label>
                  <Input
                    id="goal-form-contrib-amount"
                    data-testid="goal-form-contrib-amount"
                    type="text"
                    inputMode="decimal"
                    value={form.contribution_amount}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === "" || /^[0-9]+(?:[.,][0-9]*)?$/.test(v)) {
                        setForm({ ...form, contribution_amount: v });
                      }
                    }}
                    placeholder={tx(lang, "e.g. 500000", "ej. 500000", "ex. 500000")}
                    className="h-11 rounded-xl mt-1.5"
                  />
                </div>
                <div>
                  <Label className="text-xs">
                    {tx(lang, "Frequency", "Frecuencia", "Frequência")}
                  </Label>
                  <Select
                    value={form.contribution_frequency || "__none"}
                    onValueChange={(v) => setForm({ ...form, contribution_frequency: v === "__none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="goal-form-contrib-frequency" className="h-11 rounded-xl mt-1.5">
                      <SelectValue placeholder={tx(lang, "None", "Sin programar", "Sem agendamento")} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">{tx(lang, "None", "Sin programar", "Sem agendamento")}</SelectItem>
                      <SelectItem value="weekly">{tx(lang, "Weekly", "Semanal", "Semanal")}</SelectItem>
                      <SelectItem value="monthly">{tx(lang, "Monthly", "Mensual", "Mensal")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.contribution_amount && form.contribution_frequency ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">
                      {tx(lang, "Funding account", "Cuenta de origen", "Conta de origem")}
                    </Label>
                    <Select
                      value={form.contribution_account_id || "__primary"}
                      onValueChange={(v) => setForm({ ...form, contribution_account_id: v === "__primary" ? "" : v })}
                    >
                      <SelectTrigger data-testid="goal-form-contrib-account" className="h-11 rounded-xl mt-1.5">
                        <SelectValue placeholder={tx(lang, "Primary", "Cuenta principal", "Conta principal")} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__primary">
                          {tx(lang, "Primary (auto)", "Cuenta principal (auto)", "Conta principal (auto)")}
                        </SelectItem>
                        {cashAccounts.map((a) => (
                          <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">
                      {tx(lang, "First contribution date", "Fecha del primer aporte", "Data do primeiro aporte")}
                    </Label>
                    <DatePicker
                      testid="goal-form-contrib-start"
                      value={form.contribution_start_date}
                      onChange={(v) => setForm({ ...form, contribution_start_date: v })}
                      placeholder={tx(lang, "Pick a date", "Selecciona fecha", "Escolha uma data")}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
      </BottomSheet>

      <BottomSheet
        open={!!contribTarget}
        onOpenChange={(o) => !o && setContribTarget(null)}
        testid="goal-contrib-dialog"
        size="md"
        title={tx(lang, `Contribute to ${contribTarget?.name}`, `Aporte a ${contribTarget?.name}`, `Aporte para ${contribTarget?.name}`)}
        description={tx(lang, "Log a contribution to this goal", "Registrar un aporte a esta meta", "Registrar um aporte para essa meta")}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setContribTarget(null)} className="h-11">{t("cancel")}</Button>
            <Button data-testid="goal-contrib-confirm" onClick={submitContrib} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5 font-semibold">{tx(lang, "Contribute", "Aportar", "Aportar")}</Button>
          </div>
        }
      >
        <Label>{tx(lang, "Amount", "Monto", "Valor")}</Label>
        <Input data-testid="goal-contrib-amount" type="number" inputMode="decimal" value={contribAmount} onChange={(e) => setContribAmount(e.target.value)} autoFocus className="h-12 text-base rounded-xl mt-1.5" />
      </BottomSheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>{deleteTarget?.name}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="goal-delete-confirm" onClick={remove} className="bg-red-600 hover:bg-red-700">{t("delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
